
import { defineComponent, reactive, ref, UnwrapRef } from 'vue'
import { mapState, mapActions } from 'vuex'
import { CreateAccountPartnerClientCommandRequest } from '@/services/requests/CreateAccountPartnerClientCommandRequest'
import { TurnAccountIntoPartnerCommandRequest } from '@/services/requests/TurnAccountIntoPartnerCommandRequest'
import { AccountService } from '@/services/AccountService'
import { PartnerService } from '@/services/PartnerService'
import { IntegrationService } from '@/services/IntegrationService'
import CustomersDataTable from '@/views/configuracao/dataTable/index.vue'
import { ValidateService } from '@/services/ValidateService'

import {
    ShopOutlined,
    UserOutlined,
    CopyOutlined,
    InfoCircleTwoTone,
    ApiOutlined,
    ReadOutlined,
    InfoCircleOutlined,
    PlusOutlined,
    DeleteOutlined,
    MobileOutlined
} from '@ant-design/icons-vue'

import { notification } from 'ant-design-vue'

interface FormPartnerClient {
  company: string | null,
  cnpj: string | null,
  name: string | null,
  email: string | null,
  phone: string | null,
  cpf: string | null
}

interface PartnerPlanForm {
  uid: string,
  name: string | null,
  userNumber: number | null,
  envelopeNumber: number | null,
  whatsappNumber: number | null,
  value: number | null,
  periodTest: number | null
}

interface FormTurnPartner {
  description: string | null,
  accountCpfCnpj: string | null,
  commission: number | null,
  discount: number | null,
  plans: PartnerPlanForm[],
  featureType: number | null
}

function emptyPlan (uid: string): PartnerPlanForm {
  return {
    uid,
    name: null,
    userNumber: null,
    envelopeNumber: null,
    whatsappNumber: null,
    value: null,
    periodTest: 0
  }
}

export default defineComponent({
  name: 'PerfilConfiguration',
  components: {
    ShopOutlined,
    UserOutlined,
    CopyOutlined,
    InfoCircleTwoTone,
    ApiOutlined,
    ReadOutlined,
    InfoCircleOutlined,
    PlusOutlined,
    DeleteOutlined,
    MobileOutlined,
    CustomersDataTable
  },
  data: () => ({
    accountService: ref<AccountService>(),
    partnerService: ref<PartnerService>(),
    open: ref<boolean>(false),
    activeKey: ref('1'),
    dataSearch: '',
    searchExecuted: '',
    linkPartnerClient: ref(''),
    formPartnerClient: reactive<FormPartnerClient>({
      company: null as string | null,
      cnpj: null as string | null,
      name: '' as string,
      email: '' as string,
      cpf: '' as string,
      phone: null as string | null
    }) as UnwrapRef<FormPartnerClient>,
    partner: null,
    partnerName: '',
    partnerHash: ref(''),
    clientHash: null,
    isPartner: false,
    isFeatureApi: false,
    isShowKeysApi: false,
    accountIdApi: ref(''),
    apiKey: ref(''),
    secretKey: ref(''),
    isShowTurnPartner: false,
    isSubmittingTurnPartner: false,
    selectedCustomer: null as any,
    planUidCounter: 0,
    formTurnPartner: reactive<FormTurnPartner>({
      description: null,
      accountCpfCnpj: null,
      commission: null,
      discount: null,
      plans: [emptyPlan('plan-0')],
      featureType: null
    }) as UnwrapRef<FormTurnPartner>,

    // --- Ver dados da parceria ---
    isShowPartnerView: false,
    partnerDataView: null as any,
    currentPage: 1,
    pageSize: 6,
    total: 0
  }),
  async created () {
    this.accountService = AccountService.getInstance()
    this.partnerService = PartnerService.getInstance()

    const res = await this.partnerService.getPartnerData(this.accountInfo.id)

    this.partner = (res !== null && res.partnerId !== null) ? res : null
    this.partnerName = (res !== null && res.partnerId !== null) ? res.partnerName : ''
    this.partnerHash = (res !== null && res.partnerId !== null) ? res.partnerId : ''
    this.linkPartnerClient = (res !== null && res.partnerId !== null) ? res.partnerLink : ''
    this.isFeatureApi = (res !== null && res.isFeatureApi !== null) ? res.isFeatureApi : false
    this.clientHash = null
    this.isPartner = true
  },
  // watch: {},
  computed: {
    ...mapState('configuracao', ['accountInfo']),
    canCreate () {
      let check = false
      for (const [key, value] of Object.entries(this.formPartnerClient)) {
        if (this.activeKey === '1') {
          if (!value) check = true
        } else {
          if ((key !== 'company' && key !== 'cnpj') && !value) check = true
        }
      }
      return check
    },
    canTurnPartner () {
      const f = this.formTurnPartner
      // Campos obrigatórios: descrição, CPF/CNPJ, ao menos 1 plano válido e exatamente 1 funcionalidade.
      // Comissão, desconto e período de teste do plano são opcionais.
      if (!f.description || !f.accountCpfCnpj) return true
      if (!f.featureType) return true
      if (f.plans.length === 0) return true

      for (const plan of f.plans) {
        if (
          !plan.name ||
          plan.value === null ||
          plan.userNumber === null ||
          plan.envelopeNumber === null ||
          plan.whatsappNumber === null
        ) return true
      }
      return false
    }
  },
  methods: {
    ...mapActions('configuracao', ['setPartnerCustomers', 'setLoadingPartnerCustomers']),
    onSearch () {
      this.searchExecuted = this.dataSearch
    },
    showModalCreate () {
      this.open = true
      this.activeKey = '1'
    },
    validateCPF (cpf: string | any, isShowMessage: boolean) {
      let isValidCpf = false
      if (cpf.length === 14) {
        isValidCpf = ValidateService.isValidCpf(cpf)
        if (!isValidCpf) {
          if (isShowMessage) {
            notification.warning({
              message: 'Cpf inválido',
              description: 'O CPF inserido é inválido, favor corrigir.'
            })
          }
          return false
        } else {
            return true
        }
      } else if (cpf.length > 14) {
        if (isShowMessage) {
          notification.warning({
            message: 'Cpf inválido',
            description: 'O CPF inserido possui caracteres há mais, favor corrigir.'
          })
        }
        return false
      } else if (cpf.length < 14) {
        notification.warning({
          message: 'Cpf inválido',
          description: 'O CPF inserido possui caracteres há menos, favor corrigir.'
        })
        return false
      }
    },
    async handleOk () {
      try {
        const isCpfValid = this.validateCPF(this.formPartnerClient.cpf, true)

        if (isCpfValid) {
          const request: CreateAccountPartnerClientCommandRequest = {
            name: this.formPartnerClient.company,
            cpfCnpj: this.formPartnerClient.cnpj,
            phone: this.formPartnerClient.phone,
            email: this.formPartnerClient.email,
            cpfUser: this.formPartnerClient.cpf,
            userName: this.formPartnerClient.name,
            birthDate: null,
            password: '',
            type: parseInt(this.activeKey),
            term: '',
            geolocalization: '',
            partnerHash: (this.partnerHash !== undefined) ? this.partnerHash : null
          } as CreateAccountPartnerClientCommandRequest

          const res = await this.accountService?.createAccountPartnerClient(request)
          if (res) {
            const msg = 'Um link de verificação de conta foi enviado para o e-mail: ' + this.formPartnerClient.email
            notification.success({
              message: 'Conta criada com sucesso!',
              description: msg,
              duration: 6
            })
            // reload clients
          } else {
            notification.error({
              message: 'Erro ao cadastrar!',
              description: 'Não foi possível cadastrar esse cliente.'
            })
          }
          this.open = false
          this.clearDataClient()

          await (this.$refs.customersTable as any).reload()
        }
      } catch (err) {
        console.error(err)
      }
    },
    handleCancel () {
      this.open = false
      this.activeKey = '1'
      this.clearDataClient()
    },
    clearDataClient () {
      this.formPartnerClient = {
        company: null,
        cnpj: null,
        name: null,
        email: null,
        phone: null,
        cpf: null
      }
    },
    async copyLinkPartner () {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(this.linkPartnerClient)
            notification.success({
                message: 'O link foi copiado com sucesso!'
            })
        } else {
            const textarea = document.createElement('textarea')
            textarea.value = this.linkPartnerClient
            textarea.style.position = 'absolute'
            textarea.style.left = '-99999999px'
            document.body.prepend(textarea)
            textarea.select()
            try {
                document.execCommand('copy')
            } catch (err) {
                console.error(err)
            } finally {
                textarea.remove()
                notification.success({
                    message: 'O link foi copiado com sucesso!'
                })
            }
        }
    },
    async showModalKeysPartner (partnerHash: any) {
      const response = await IntegrationService.getInstance().getIntegrationPartnerData(partnerHash)
      this.isShowKeysApi = true
      this.accountIdApi = response.id
      this.apiKey = response.apiKey
      this.secretKey = response.secretKey
    },
    async copyAccoundId () {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(this.accountIdApi)
            notification.success({
                message: 'O Hash foi copiado com sucesso!'
            })
        } else {
            const textarea = document.createElement('textarea')
            textarea.value = this.accountIdApi
            textarea.style.position = 'absolute'
            textarea.style.left = '-99999999px'
            document.body.prepend(textarea)
            textarea.select()
            try {
                document.execCommand('copy')
            } catch (err) {
                console.error(err)
            } finally {
                textarea.remove()
                notification.success({
                    message: 'O Hash foi copiado com sucesso!'
                })
            }
        }
    },
    async copyApiKey () {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(this.apiKey)
            notification.success({
                message: 'A chave de API foi copiada com sucesso!'
            })
        } else {
            const textarea = document.createElement('textarea')
            textarea.value = this.apiKey
            textarea.style.position = 'absolute'
            textarea.style.left = '-99999999px'
            document.body.prepend(textarea)
            textarea.select()
            try {
                document.execCommand('copy')
            } catch (err) {
                console.error(err)
            } finally {
                textarea.remove()
                notification.success({
                    message: 'A chave de API foi copiada com sucesso!'
                })
            }
        }
    },
    async copySecretKey () {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(this.secretKey)
            notification.success({
                message: 'A chave secreta foi copiada com sucesso!'
            })
        } else {
            const textarea = document.createElement('textarea')
            textarea.value = this.secretKey
            textarea.style.position = 'absolute'
            textarea.style.left = '-99999999px'
            document.body.prepend(textarea)
            textarea.select()
            try {
                document.execCommand('copy')
            } catch (err) {
                console.error(err)
            } finally {
                textarea.remove()
                notification.success({
                    message: 'A chave secreta foi copiada com sucesso!'
                })
            }
        }
    },

    // --- Tornar cliente em parceiro ---
    showModalTurnPartner (customer: any) {
      this.selectedCustomer = customer
      this.planUidCounter = 0
      this.formTurnPartner.description = null
      this.formTurnPartner.accountCpfCnpj = null
      this.formTurnPartner.commission = 0
      this.formTurnPartner.discount = 0
      this.formTurnPartner.plans = [emptyPlan('plan-0')]
      this.formTurnPartner.featureType = null
      this.isShowTurnPartner = true
    },
    addPlan () {
      this.planUidCounter += 1
      this.formTurnPartner.plans.push(emptyPlan('plan-' + this.planUidCounter))
    },
    removePlan (index: number) {
      this.formTurnPartner.plans.splice(index, 1)
    },
    handleCancelTurnPartner () {
      this.isShowTurnPartner = false
      this.selectedCustomer = null
    },
    async handleOkTurnPartner () {
      if (this.canTurnPartner || !this.selectedCustomer) return

      this.isSubmittingTurnPartner = true
      try {
        const partnerFeatures = [{ featureId: this.formTurnPartner.featureType as number }]

        const request: TurnAccountIntoPartnerCommandRequest = {
          description: this.formTurnPartner.description as string,
          account: {
            id: this.selectedCustomer.id,
            name: this.selectedCustomer.name,
            cpfCnpj: this.formTurnPartner.accountCpfCnpj as string
          },
          parameterization: {
            commission: this.formTurnPartner.commission,
            discount: this.formTurnPartner.discount
          },
          plans: this.formTurnPartner.plans.map(plan => ({
            name: plan.name as string,
            userNumber: plan.userNumber as number,
            envelopeNumber: plan.envelopeNumber as number,
            whatsappNumber: plan.whatsappNumber as number,
            value: plan.value as number,
            periodTest: plan.periodTest
          })),
          partnerFeatures
        } as TurnAccountIntoPartnerCommandRequest

        const res = await this.partnerService?.turnAccountIntoPartner(request)
        if (res) {
          notification.success({
            message: 'Cliente promovido a parceiro!',
            description: `${this.selectedCustomer.name} agora é um parceiro.`,
            duration: 6
          })
          this.isShowTurnPartner = false

          await (this.$refs.customersTable as any).reload(this.selectedCustomer.id)
        } else {
          notification.error({
            message: 'Erro ao promover parceiro!',
            description: 'Não foi possível tornar esse cliente um parceiro.'
          })
        }
      } catch (err) {
        notification.error({
          message: 'Erro ao promover parceiro!',
          description: 'Ocorreu um erro inesperado, tente novamente.'
        })
      } finally {
        this.isSubmittingTurnPartner = false
      }
    },

    // --- Ver dados da parceria ---
    async showModalViewPartner (customer: any) {
      this.selectedCustomer = customer
      try {
        const data = await this.partnerService?.getPartnerParameterization(customer.id)
        this.partnerDataView = data
        this.isShowPartnerView = true
      } catch (err) {
        notification.error({
          message: 'Erro ao consultar parceria!',
          description: 'Não foi possível carregar os dados dessa parceria.'
        })
      }
    },
    hasFeature (featureId: number): boolean {
      if (!this.partnerDataView || !this.partnerDataView.features) {
        return false
      }

      return this.partnerDataView.features.includes(featureId)
    }
  }
})
